/**
 * Role views — the role-first UI scoping model (Design Principle #1).
 *
 * A "role view" is a *presentation-layer* persona that trims the left nav to a
 * role's relevant surfaces and redirects to that role's landing page. It is
 * deliberately DECOUPLED from the backend auth `Role`: the logged-in staff
 * token (and tenant) never changes when the view switches, so every API call
 * still succeeds. Switching a view is pure UI scoping for the client tour — it
 * is not a re-auth and must never cause a scoped persona (e.g. `cfo`) to 403.
 *
 * `all` (Admin / All) is the silent boot DEFAULT and shows every nav item — the
 * full client tour. It is also the first selectable option in the top-bar
 * dropdown (see `ROLE_VIEW_ORDER`) so an operator can always return to the full
 * rail after switching into a scoped persona. Admin-oriented surfaces — the
 * Onboarding sub-page and Intelligence & Model Governance (`/intelligence`, the
 * AI-insights view) — appear ONLY in `all`; scoped personas intentionally omit
 * them to keep each role's rail focused on its own work.
 */
import { NAV_ITEMS, type NavItem } from './nav';

/** A nav item's route path doubles as its stable key. */
export type NavKey = NavItem['to'];

/** UI scoping personas selectable in the top-bar role switcher. */
export type RoleViewKey =
  | 'all'
  | 'pas'
  | 'bas'
  | 'finance'
  | 'retention'
  | 'state_ops'
  | 'clinical'
  | 'supervisor'
  | 'cfo';

export interface RoleView {
  key: RoleViewKey;
  /** Human label shown in the top-bar switcher. */
  label: string;
  /** Landing route the switcher navigates to when this view is selected. */
  landing: string;
  /** Nav routes (NAV_ITEMS `to`) visible in this view. Empty ⇒ unfiltered. */
  nav: NavKey[];
}

/**
 * Role-view registry. `all` keeps `nav: []` as a sentinel for "unfiltered"
 * (the full NAV_ITEMS list) so the default tour never drifts out of sync if a
 * new nav item is added.
 */
export const ROLE_VIEWS: Record<RoleViewKey, RoleView> = {
  all: {
    key: 'all',
    label: 'Admin / All',
    landing: '/queues',
    nav: [],
  },
  pas: {
    key: 'pas',
    label: 'Patient Advocate',
    landing: '/queues',
    // Employers/AI-review are no longer rail tabs but stay reachable inside a case.
    nav: ['/queues', '/cases', '/employers', '/ai-review', '/notifications'],
  },
  bas: {
    key: 'bas',
    label: 'Benefits Advisor',
    landing: '/cases',
    nav: ['/queues', '/cases', '/employers', '/notifications'],
  },
  finance: {
    key: 'finance',
    label: 'Finance',
    landing: '/billing',
    nav: ['/billing', '/cases', '/reports', '/invoices', '/notifications'],
  },
  retention: {
    key: 'retention',
    label: 'Retention',
    landing: '/queues',
    nav: ['/queues', '/cases', '/retention', '/notifications'],
  },
  state_ops: {
    key: 'state_ops',
    label: 'State Ops',
    landing: '/state-ops',
    nav: ['/state-ops', '/cases', '/notifications'],
  },
  clinical: {
    key: 'clinical',
    label: 'Case Mgmt',
    landing: '/cases',
    nav: ['/cases', '/queues', '/ai-review', '/notifications'],
  },
  supervisor: {
    key: 'supervisor',
    label: 'Supervisor',
    landing: '/team',
    nav: ['/team', '/queues', '/reports', '/cases', '/notifications'],
  },
  cfo: {
    key: 'cfo',
    label: 'Hospital CFO',
    landing: '/cfo-portal',
    nav: ['/cfo-portal', '/reports'],
  },
};

/**
 * Display order for the top-bar switcher. `all` (Admin / All) is listed first so
 * an operator can always return to the full, unscoped tour after switching into
 * a scoped persona — without it, there is no way back to the complete rail short
 * of a re-login. It remains the silent boot default (`DEFAULT_ROLE_VIEW`).
 */
export const ROLE_VIEW_ORDER: RoleViewKey[] = [
  'all',
  'pas',
  'bas',
  'finance',
  'retention',
  'state_ops',
  'clinical',
  'supervisor',
  'cfo',
];

export const DEFAULT_ROLE_VIEW: RoleViewKey = 'all';

export function isRoleViewKey(value: string | null | undefined): value is RoleViewKey {
  return !!value && value in ROLE_VIEWS;
}

export function getRoleView(key: RoleViewKey): RoleView {
  return ROLE_VIEWS[key];
}

/**
 * NAV_ITEMS filtered to a view (the `all` view is returned unfiltered). A parent
 * is kept if the parent route is allowed OR any of its children are; disallowed
 * children are trimmed so a scoped view never surfaces a sub-page it can't reach.
 */
export function navItemsForView(key: RoleViewKey): NavItem[] {
  const view = ROLE_VIEWS[key];
  if (view.nav.length === 0) return NAV_ITEMS;
  const allowed = new Set(view.nav);
  return NAV_ITEMS.reduce<NavItem[]>((acc, item) => {
    const kids = item.children?.filter((c) => allowed.has(c.to));
    if (allowed.has(item.to) || (kids && kids.length > 0)) {
      acc.push({ ...item, children: kids && kids.length > 0 ? kids : undefined });
    }
    return acc;
  }, []);
}

/**
 * Whether a path is reachable in the given view. The `all` view allows
 * everything. A scoped view allows its own nav routes plus shared/sub routes
 * (case detail, the queues alias, etc.) so deep navigation inside an allowed
 * surface is not bounced.
 */
export function isPathAllowedInView(key: RoleViewKey, pathname: string): boolean {
  const view = ROLE_VIEWS[key];
  if (view.nav.length === 0) return true;
  const top = '/' + (pathname.split('/')[1] ?? '');
  return view.nav.includes(top);
}
